import React, { useMemo, useState } from "react";
import { Card, Row, Col, Form, Table, Badge } from "react-bootstrap";
import Select from "react-select";
import Header from "../../layouts/Header";

const ProfitLossReport = () => {
  const [skin, setSkin] = useState(localStorage.getItem("skin-mode") ? "dark" : "");
  const [selectedOutlet, setSelectedOutlet] = useState("ALL");

  const outlets = [
    {
      id: "outlet_001",
      name: "Xpanse Cafe - Panjim",
      grossSales: 485000,
      discounts: 32000,
      taxCollected: 22800,
      cogs: 168000,
      packagingCost: 18500,
      deliveryCost: 26500,
      platformCharges: 42000,
      staffCost: 78000,
      rent: 55000,
      utilities: 18500,
      otherExpenses: 12000,
    },
    {
      id: "outlet_002",
      name: "Xpanse Cafe - Mapusa",
      grossSales: 365000,
      discounts: 21000,
      taxCollected: 17400,
      cogs: 132000,
      packagingCost: 14200,
      deliveryCost: 19800,
      platformCharges: 33500,
      staffCost: 62000,
      rent: 42000,
      utilities: 14500,
      otherExpenses: 9500,
    },
    {
      id: "outlet_003",
      name: "Xpanse Cafe - Margao",
      grossSales: 525000,
      discounts: 45500,
      taxCollected: 24100,
      cogs: 192000,
      packagingCost: 21000,
      deliveryCost: 29500,
      platformCharges: 48500,
      staffCost: 85000,
      rent: 60000,
      utilities: 22000,
      otherExpenses: 15000,
    },
  ];

  const outletOptions = [
    { value: "ALL", label: "All Outlets" },
    ...outlets.map((outlet) => ({
      value: outlet.id,
      label: outlet.name,
    })),
  ];

  const formatCurrency = (value) => {
    return `₹${Number(value || 0).toLocaleString("en-IN", {
      maximumFractionDigits: 2,
    })}`;
  };

  const formatPercent = (value) => {
    return `${Number(value || 0).toFixed(1)}%`;
  };

  const calculatePNL = (outlet) => {
    const netSales = outlet.grossSales - outlet.discounts;
    const grossProfit = netSales - outlet.cogs;

    const totalOperatingExpenses =
      outlet.packagingCost +
      outlet.deliveryCost +
      outlet.platformCharges +
      outlet.staffCost +
      outlet.rent +
      outlet.utilities +
      outlet.otherExpenses;

    const netProfit = grossProfit - totalOperatingExpenses;

    const grossMargin = netSales > 0 ? (grossProfit / netSales) * 100 : 0;
    const netProfitMargin = netSales > 0 ? (netProfit / netSales) * 100 : 0;
    const cogsPercent = netSales > 0 ? (outlet.cogs / netSales) * 100 : 0;
    const expensePercent = netSales > 0 ? (totalOperatingExpenses / netSales) * 100 : 0;

    return {
      ...outlet,
      netSales,
      grossProfit,
      totalOperatingExpenses,
      netProfit,
      grossMargin,
      netProfitMargin,
      cogsPercent,
      expensePercent,
    };
  };

  const pnlData = useMemo(() => {
    const filtered =
      selectedOutlet === "ALL"
        ? outlets
        : outlets.filter((outlet) => outlet.id === selectedOutlet);

    return filtered.map(calculatePNL);
  }, [selectedOutlet]);

  const summary = useMemo(() => {
    const total = pnlData.reduce(
      (acc, outlet) => {
        acc.grossSales += outlet.grossSales;
        acc.discounts += outlet.discounts;
        acc.netSales += outlet.netSales;
        acc.taxCollected += outlet.taxCollected;
        acc.cogs += outlet.cogs;
        acc.grossProfit += outlet.grossProfit;
        acc.totalOperatingExpenses += outlet.totalOperatingExpenses;
        acc.netProfit += outlet.netProfit;
        return acc;
      },
      {
        grossSales: 0,
        discounts: 0,
        netSales: 0,
        taxCollected: 0,
        cogs: 0,
        grossProfit: 0,
        totalOperatingExpenses: 0,
        netProfit: 0,
      }
    );

    total.grossMargin =
      total.netSales > 0 ? (total.grossProfit / total.netSales) * 100 : 0;

    total.netProfitMargin =
      total.netSales > 0 ? (total.netProfit / total.netSales) * 100 : 0;

    return total;
  }, [pnlData]);

  return (
    <React.Fragment>
      <Header onSkin={setSkin} />

      <div className="main main-app p-3 p-lg-4">
        <div className="container-fluid bg-white shadow-sm rounded-3 p-4">
          <Row className="align-items-center mb-3">
            <Col>
              <h5 className="fw-semibold mb-1" style={{ fontSize: "18px" }}>
                OUTLET P&L REPORT
              </h5>
              <div className="text-muted" style={{ fontSize: "12px" }}>
                Outlet-wise profit and loss summary based on sales, costing and operating expenses
              </div>
            </Col>

            <Col md={3}>
              <Form.Label className="text-muted fw-semibold mb-1" style={{ fontSize: "12px" }}>
                Outlet
              </Form.Label>
              <Select
                options={outletOptions}
                value={outletOptions.find((option) => option.value === selectedOutlet)}
                onChange={(option) => setSelectedOutlet(option?.value || "ALL")}
                placeholder="Select Outlet"
                styles={{
                  control: (base) => ({
                    ...base,
                    fontSize: "13px",
                    minHeight: "36px",
                  }),
                  menu: (base) => ({
                    ...base,
                    fontSize: "13px",
                  }),
                }}
              />
            </Col>
          </Row>

          <hr />

          <Row className="g-3 mb-4">
            <Col md={3}>
              <Card className="border-0 shadow-sm">
                <Card.Body>
                  <div className="text-muted small">Net Sales</div>
                  <h5 className="fw-bold mb-0 text-primary">
                    {formatCurrency(summary.netSales)}
                  </h5>
                </Card.Body>
              </Card>
            </Col>

            <Col md={3}>
              <Card className="border-0 shadow-sm">
                <Card.Body>
                  <div className="text-muted small">COGS</div>
                  <h5 className="fw-bold mb-0 text-danger">
                    {formatCurrency(summary.cogs)}
                  </h5>
                </Card.Body>
              </Card>
            </Col>

            <Col md={3}>
              <Card className="border-0 shadow-sm">
                <Card.Body>
                  <div className="text-muted small">Gross Profit</div>
                  <h5 className="fw-bold mb-0 text-success">
                    {formatCurrency(summary.grossProfit)}
                  </h5>
                  <div className="small text-muted">
                    Margin: {formatPercent(summary.grossMargin)}
                  </div>
                </Card.Body>
              </Card>
            </Col>

            <Col md={3}>
              <Card className="border-0 shadow-sm">
                <Card.Body>
                  <div className="text-muted small">Net Profit</div>
                  <h5
                    className={`fw-bold mb-0 ${
                      summary.netProfit >= 0 ? "text-success" : "text-danger"
                    }`}
                  >
                    {formatCurrency(summary.netProfit)}
                  </h5>
                  <div className="small text-muted">
                    Margin: {formatPercent(summary.netProfitMargin)}
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          <Card className="shadow-sm border-0">
            <Card.Header className="bg-white">
              <h6 className="mb-0 fw-semibold" style={{ fontSize: "15px" }}>
                Outlet-wise P&L
              </h6>
            </Card.Header>

            <Card.Body>
              <div className="table-responsive">
                <Table hover size="sm" className="align-middle">
                  <thead>
                    <tr style={{ fontSize: "12px" }}>
                      <th>Outlet</th>
                      <th className="text-end">Gross Sales</th>
                      <th className="text-end">Discounts</th>
                      <th className="text-end">Net Sales</th>
                      <th className="text-end">Tax</th>
                      <th className="text-end">COGS</th>
                      <th className="text-end">Gross Profit</th>
                      <th className="text-end">Gross Margin</th>
                      <th className="text-end">Expenses</th>
                      <th className="text-end">Net Profit</th>
                      <th className="text-end">Net Margin</th>
                      <th className="text-center">Status</th>
                    </tr>
                  </thead>

                  <tbody style={{ fontSize: "12px" }}>
                    {pnlData.map((outlet) => (
                      <tr key={outlet.id}>
                        <td>
                          <div className="fw-semibold">{outlet.name}</div>
                          <div className="text-muted small">
                            COGS: {formatPercent(outlet.cogsPercent)} | Expenses:{" "}
                            {formatPercent(outlet.expensePercent)}
                          </div>
                        </td>

                        <td className="text-end">{formatCurrency(outlet.grossSales)}</td>
                        <td className="text-end text-danger">
                          -{formatCurrency(outlet.discounts)}
                        </td>
                        <td className="text-end fw-semibold">
                          {formatCurrency(outlet.netSales)}
                        </td>
                        <td className="text-end">{formatCurrency(outlet.taxCollected)}</td>
                        <td className="text-end text-danger">
                          {formatCurrency(outlet.cogs)}
                        </td>
                        <td className="text-end text-success fw-semibold">
                          {formatCurrency(outlet.grossProfit)}
                        </td>
                        <td className="text-end">{formatPercent(outlet.grossMargin)}</td>
                        <td className="text-end text-danger">
                          {formatCurrency(outlet.totalOperatingExpenses)}
                        </td>
                        <td
                          className={`text-end fw-bold ${
                            outlet.netProfit >= 0 ? "text-success" : "text-danger"
                          }`}
                        >
                          {formatCurrency(outlet.netProfit)}
                        </td>
                        <td className="text-end">
                          {formatPercent(outlet.netProfitMargin)}
                        </td>
                        <td className="text-center">
                          {outlet.netProfit >= 0 ? (
                            <Badge bg="success">Profitable</Badge>
                          ) : (
                            <Badge bg="danger">Loss</Badge>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </Card.Body>
          </Card>

          <Row className="g-3 mt-4">
            {pnlData.map((outlet) => (
              <Col md={4} key={`${outlet.id}-breakup`}>
                <Card className="shadow-sm border-0 h-100">
                  <Card.Header className="bg-white">
                    <div className="fw-semibold" style={{ fontSize: "14px" }}>
                      {outlet.name}
                    </div>
                    <div className="text-muted small">Expense Breakup</div>
                  </Card.Header>

                  <Card.Body>
                    <div className="d-flex justify-content-between mb-2">
                      <span>Packaging</span>
                      <strong>{formatCurrency(outlet.packagingCost)}</strong>
                    </div>

                    <div className="d-flex justify-content-between mb-2">
                      <span>Delivery</span>
                      <strong>{formatCurrency(outlet.deliveryCost)}</strong>
                    </div>

                    <div className="d-flex justify-content-between mb-2">
                      <span>Platform Charges</span>
                      <strong>{formatCurrency(outlet.platformCharges)}</strong>
                    </div>

                    <div className="d-flex justify-content-between mb-2">
                      <span>Staff Cost</span>
                      <strong>{formatCurrency(outlet.staffCost)}</strong>
                    </div>

                    <div className="d-flex justify-content-between mb-2">
                      <span>Rent</span>
                      <strong>{formatCurrency(outlet.rent)}</strong>
                    </div>

                    <div className="d-flex justify-content-between mb-2">
                      <span>Utilities</span>
                      <strong>{formatCurrency(outlet.utilities)}</strong>
                    </div>

                    <div className="d-flex justify-content-between mb-2">
                      <span>Other Expenses</span>
                      <strong>{formatCurrency(outlet.otherExpenses)}</strong>
                    </div>

                    <hr />

                    <div className="d-flex justify-content-between">
                      <span className="fw-semibold">Total Expenses</span>
                      <strong className="text-danger">
                        {formatCurrency(outlet.totalOperatingExpenses)}
                      </strong>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        </div>
      </div>
    </React.Fragment>
  );
};

export default ProfitLossReport;